import React, {
  forwardRef,
  useImperativeHandle,
  useContext,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';

import {
  AddItemLarge,
  AddItemMedium,
  AddItemSmall,
  AddMedia,
} from '@wix/wix-ui-icons-common/system';

import { Add } from '@wix/wix-ui-icons-common';

import { WixStyleReactContext } from '../WixStyleReactProvider/context';
import { useFocusRing } from '../providers/useFocusRing/useFocusRing';

import Tooltip from '../Tooltip';
import Text from '../Text';
import Image from '../Image';

import { dataHooks } from './constants';

import { st, classes } from './AddItem.st.css';
import { AddItemProps, AddItemActions } from './AddItem.types';

const AddItemButtonIcons = {
  tiny: ({ className }: { className: string }) => (
    <Add
      data-hook={dataHooks.defaultIcon}
      className={className}
      width="24"
      height="24"
    />
  ),
  small: () => <AddItemSmall data-hook={dataHooks.defaultIcon} />,
  medium: () => <AddItemMedium data-hook={dataHooks.defaultIcon} />,
  large: () => <AddItemLarge data-hook={dataHooks.defaultIcon} />,
  image: ({ className }: { className: string }) => (
    <AddMedia
      data-hook={dataHooks.defaultIcon}
      className={className}
      width="31"
      height="31"
    />
  ),
} as const;

const illustrationDimensions = {
  tiny: { height: 24, width: 24 },
  small: { height: 60, width: 60 },
  medium: { height: 120, width: 120 },
  large: { height: 120, width: 120 },
};

const tooltipPlacementByAlignment = {
  left: 'top-start',
  right: 'top-end',
  center: 'top',
} as const;

const AddItem = forwardRef<AddItemActions, AddItemProps>(
  (
    {
      theme = 'dashes',
      size = 'tiny',
      alignItems = 'center',
      removePadding = false,
      borderRadius,
      disabled,
      className,
      onClick,
      ariaLabel,
      icon,
      children,
      subtitle,
      dataHook,
      tooltipProps,
      tooltipContent,
      showIcon = true,
    },
    ref,
  ) => {
    const { newColorsBranding } = useContext(WixStyleReactContext);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        buttonRef.current?.focus();
      },
      blur: () => {
        buttonRef.current?.blur();
      },
    }));

    const iconElement = useMemo(() => {
      if (!showIcon) {
        return;
      }
      if (!icon) {
        const isImageIcon = theme === 'image';
        const Icon = AddItemButtonIcons[isImageIcon ? 'image' : size];
        return <Icon className={classes.icon} />;
      }
      if (typeof icon === 'string') {
        return (
          <Image
            className={classes.illustration}
            fit="contain"
            src={icon}
            {...illustrationDimensions[size]}
          />
        );
      }
      return icon;
    }, [icon, size, theme, showIcon]);

    const textElement = useMemo(() => {
      if (!children || theme === 'image') {
        return;
      }
      const textSize = size === 'tiny' ? 'small' : 'medium';
      return (
        <div className={st(classes.textWrapper, { size })}>
          {typeof children === 'function' ? (
            children()
          ) : (
            <Text
              className={classes.textContent}
              weight="thin"
              skin="standard"
              size={textSize}
              dataHook={dataHooks.itemText}
              ellipsis
            >
              {children}
            </Text>
          )}
        </div>
      );
    }, [children, size, theme]);

    const subtitleElement = useMemo(() => {
      return (
        subtitle && (
          <Text
            className={st(classes.subtitle, { size })}
            size="small"
            dataHook={dataHooks.itemSubtitle}
          >
            {subtitle}
          </Text>
        )
      );
    }, [subtitle, size]);

    const commonButtonProps = {
      className,
      onClick,
      disabled,
      theme,
      size,
      ariaLabel,
      borderRadius,
      removePadding,
      newColorsBranding,
      alignItems,
      icon: iconElement,
      text: textElement,
      subtitle: subtitleElement,
    };

    // TODO: remove this once we deprecate content usage thhrough tooltip
    // @ts-expect-error
    const tooltip = tooltipContent || tooltipProps?.content;

    return (
      <div style={{ width: '100%', height: '100%' }} data-hook={dataHook}>
        {tooltip && (
          <Tooltip
            content={tooltip}
            className={classes.tooltip}
            flip={theme === 'image'}
            moveBy={{ y: theme === 'image' ? -12 : 0 }}
            dataHook={dataHooks.itemTooltip}
            placement={tooltipPlacementByAlignment[alignItems]}
            {...tooltipProps}
          >
            <AddItemButton ref={buttonRef} {...commonButtonProps} />
          </Tooltip>
        )}
        {!tooltip && <AddItemButton ref={buttonRef} {...commonButtonProps} />}
      </div>
    );
  },
);

const AddItemButton = forwardRef<
  HTMLButtonElement,
  {
    className?: AddItemProps['className'];
    onClick: AddItemProps['onClick'];
    disabled: AddItemProps['disabled'];
    theme: AddItemProps['theme'];
    size: AddItemProps['size'];
    ariaLabel: AddItemProps['ariaLabel'];
    borderRadius: AddItemProps['borderRadius'];
    removePadding: AddItemProps['removePadding'];
    newColorsBranding: boolean;
    alignItems: AddItemProps['alignItems'];
    icon: React.ReactNode;
    text: React.ReactNode;
    subtitle: React.ReactNode;
  }
>(
  (
    {
      className,
      onClick,
      disabled,
      size,
      ariaLabel,
      borderRadius,
      alignItems = 'center',
      newColorsBranding,
      removePadding,
      theme,
      icon,
      text,
      subtitle,
    },
    ref,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing();
    return (
      <button
        ref={ref}
        data-hook={dataHooks.addItem}
        className={st(
          classes.root,
          {
            theme,
            size,
            removePadding,
            borderRadius,
            disabled,
            alignItems,
            newColorsBranding,
            'focus-visible': isFocusVisible,
          },
          className,
        )}
        style={{ borderRadius }}
        disabled={disabled}
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        {...focusProps}
      >
        <div
          className={st(classes.contentContainer, {
            theme,
            size,
            alignItems,
            disabled,
          })}
        >
          <div className={st(classes.content, { size })}>
            {icon}
            {text}
          </div>
          {subtitle}
        </div>
      </button>
    );
  },
);

AddItem.displayName = 'AddItem';

AddItem.propTypes = {
  // @ts-ignore
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  disabled: PropTypes.bool,
  theme: PropTypes.oneOf(['dashes', 'plain', 'filled', 'image']),
  alignItems: PropTypes.oneOf(['center', 'right', 'left']),
  size: PropTypes.oneOf(['large', 'medium', 'small', 'tiny']),
  onClick: PropTypes.func,
  dataHook: PropTypes.string,
  className: PropTypes.string,
  // @ts-ignore
  tooltipContent: PropTypes.node,
  tooltipProps: PropTypes.object,
  showIcon: PropTypes.bool,
  removePadding: PropTypes.bool,
  borderRadius: PropTypes.string,
  ariaLabel: PropTypes.string,
  // @ts-ignore
  subtitle: PropTypes.node,
  // @ts-ignore
  icon: PropTypes.node,
};

export default AddItem;
